import React, { memo, useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { SharedValue, interpolate, useAnimatedStyle } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import { FavoriteSelected } from '@/assets/icons/favorite-selected';
import FavoriteUnselected from '@/assets/icons/favorite-unselected';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useFavorites } from '@/feature/betslip-pickem/components/FavoritesProvider';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { findEnabledLobbyComponentByOrderingKey } from '@/feature/lobby/hooks/use-lobby-query-variables';
import { DEFAULT_LOBBY_ORDERING, LobbyComponent } from '@/feature/lobby/utils/lobbySections';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { isWeb } from '@/utils/constants-platform-specific';
import { formatPlayerName } from '@/utils/format-player-name';

export const PlayerProfileTitleWithScrollableModal = ({
    playerInfo,
    onClose,
    fromSinglePlayerProfile,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    onClose?: () => void;
    fromSinglePlayerProfile?: boolean;
}) => {
    const { scrollOffset } = useScrollableModalContext();

    return (
        <PlayerProfileTitle
            scrollOffset={scrollOffset}
            fromSinglePlayerProfile={fromSinglePlayerProfile}
            onClose={onClose}
            playerInfo={playerInfo}
        />
    );
};

export const PlayerProfileTitle = ({
    playerInfo,
    onClose,
    fromSinglePlayerProfile,
    scrollOffset,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    onClose?: () => void;
    fromSinglePlayerProfile?: boolean;
    scrollOffset: SharedValue<number>;
}) => {
    const { favoritesIds, addFavorite, removeFavorite } = useFavorites();
    const guest = useUserStore(state => state.guest);
    const lobbyComponents = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.lobbyComponents?.items ?? DEFAULT_LOBBY_ORDERING
    );
    const isFavoritesEnabled = findEnabledLobbyComponentByOrderingKey(lobbyComponents, LobbyComponent.FavoritesPlayers);
    const shouldDisplayFavoriteIcon = !guest && isFavoritesEnabled;
    const isFavorite = favoritesIds.includes(playerInfo?.id);

    const addToFavorites = () => {
        addFavorite(playerInfo?.id);
    };

    const removeFromFavorites = () => {
        removeFavorite(playerInfo?.id);
    };

    const headerTitleStyle = useAnimatedStyle(() => {
        return {
            opacity: interpolate(scrollOffset.value, [0, 90, 100], [0, 0, 1]),
        };
    });

    return (
        <Animated.View>
            <Row
                paddingHorizontal={'s16'}
                paddingVertical={'s12'}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                {!fromSinglePlayerProfile ? (
                    <SyncedSheetCloseButton />
                ) : (
                    <TouchableOpacity onPress={onClose} hitSlop={12}>
                        <CloseSmallIcon />
                    </TouchableOpacity>
                )}
                <Animated.View style={[styles.titleTextContainer, headerTitleStyle]}>
                    <Text variant={'titleLarge'} numberOfLines={1}>
                        {formatPlayerName(playerInfo, false)}
                    </Text>
                </Animated.View>
                {shouldDisplayFavoriteIcon ? (
                    <Box>
                        <TouchableOpacity onPress={isFavorite ? removeFromFavorites : addToFavorites}>
                            {isFavorite ? (
                                <FavoriteSelected />
                            ) : (
                                <FavoriteUnselected color={ds.colorValue.icon.primary} />
                            )}
                        </TouchableOpacity>
                    </Box>
                ) : null}
            </Row>
        </Animated.View>
    );
};

const SyncedSheetCloseButton = memo(() => {
    const { bottomSheetCircularPagerRef } = useFullScreenPagerTransition();

    const navigation = useNavigation();

    // on web we navigate back
    // on mobile we close the bottom sheet
    const onClose = useCallback(() => {
        if (isWeb) {
            navigation.goBack();
            return;
        }
        bottomSheetCircularPagerRef.current?.close();
    }, [bottomSheetCircularPagerRef, navigation]);

    return (
        <TouchableOpacity onPress={onClose} hitSlop={12}>
            <CloseSmallIcon />
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    titleTextContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
